import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { Wrench, ClipboardList, CheckCircle, Calendar, ArrowRight, Loader2, Clock, User } from "lucide-react";
import { API_URL } from "@/config";
import { Card } from "@/components/ui/card";

interface RepairOrder {
    ro_id: number;
    status: string;
    tech_staff_id: number | null;
    vehicle_desc: string | null;
    total_amount: string | null;
    ro_notes: string | null;
    created_at: string;
    updated_at: string;
    appointment: {
        customer_name: string | null;
        customer_phone: string | null;
        appt_date: string | null;
        appt_time: string | null;
        service_type: string | null;
    } | null;
}

interface Appointment {
    appt_id: number;
    customer_name: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_year: number | null;
    service_type: string | null;
    appt_date: string | null;
    appt_time: string | null;
    status: string;
}

const STATUS_COLOR: Record<string, string> = {
    OPEN:        "bg-blue-100 text-blue-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    COMPLETED:   "bg-teal-100 text-teal-700",
    INVOICED:    "bg-violet-100 text-violet-700",
    PAID:        "bg-green-100 text-green-700",
};

const APPT_STATUS_COLOR: Record<string, string> = {
    SCHEDULED:   "bg-blue-100 text-blue-700",
    CONFIRMED:   "bg-indigo-100 text-indigo-700",
    IN_PROGRESS: "bg-orange-100 text-orange-700",
    COMPLETED:   "bg-green-100 text-green-700",
    CANCELLED:   "bg-gray-100 text-gray-500",
};

export default function TechnicanDashboard() {
    const [staff,       setStaff]       = useState<StaffUser | null>(null);
    const [allOrders,   setAllOrders]   = useState<RepairOrder[]>([]);
    const [appointments,setAppointments]= useState<Appointment[]>([]);
    const [loading,     setLoading]     = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentStaff().then((r) => { if (r.ok && r.staff) setStaff(r.staff); }),
            axios.get(`${API_URL}/repair-shops/staff/repair-orders`, { withCredentials: true })
                .then((r) => setAllOrders(r.data.repair_orders ?? []))
                .catch(() => null),
            axios.get(`${API_URL}/repair-shops/staff/appointments`, { withCredentials: true })
                .then((r) => setAppointments(r.data.appointments ?? []))
                .catch(() => null),
        ]).finally(() => setLoading(false));
    }, []);

    // My assigned ROs — filter by tech_staff_id after staff loads
    const myOrders = staff
        ? allOrders.filter((ro) => ro.tech_staff_id === staff.staff_id)
        : [];

    const myOpen        = myOrders.filter((ro) => ro.status === "OPEN").length;
    const myInProgress  = myOrders.filter((ro) => ro.status === "IN_PROGRESS").length;
    const myDone        = myOrders.filter((ro) => ["INVOICED", "PAID", "COMPLETED"].includes(ro.status)).length;

    const today = new Date().toISOString().slice(0, 10);
    const todayAppts = appointments.filter((a) => a.appt_date === today);

    // Show assigned first, then unassigned, capped at 8
    const displayOrders = [
        ...myOrders.filter((ro) => ["OPEN", "IN_PROGRESS"].includes(ro.status)),
        ...allOrders.filter((ro) => !ro.tech_staff_id && ["OPEN", "IN_PROGRESS"].includes(ro.status)),
    ].slice(0, 8);

    const statCards = [
        { label: "My Open ROs",      value: myOpen,       icon: ClipboardList, color: "text-orange-500", bg: "bg-orange-50" },
        { label: "In Progress",      value: myInProgress, icon: Wrench,        color: "text-blue-500",   bg: "bg-blue-50"   },
        { label: "Completed",        value: myDone,       icon: CheckCircle,   color: "text-green-500",  bg: "bg-green-50"  },
        { label: "Appts Today",      value: todayAppts.length, icon: Calendar,  color: "text-violet-500", bg: "bg-violet-50" },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-semibold">
                    {staff ? `Welcome, ${staff.first_name}` : "Technician Dashboard"}
                </h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                    {staff ? `Technician · ${staff.role.replace(/_/g, " ")}` : "Loading…"}
                </p>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">
                            {loading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : value}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-3">
                <Link
                    to="/technican/repair-orders"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <ClipboardList className="h-4 w-4" /> All Repair Orders
                </Link>
                <Link
                    to="/technican/appointments"
                    className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                >
                    <Calendar className="h-4 w-4" /> Appointments
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Work queue */}
                <Card className="lg:col-span-2 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b">
                        <h2 className="font-semibold">Work Queue</h2>
                        <Link
                            to="/technican/repair-orders"
                            className="text-xs text-orange-600 hover:underline flex items-center gap-1"
                        >
                            View all <ArrowRight className="h-3 w-3" />
                        </Link>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-10">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : displayOrders.length === 0 ? (
                        <div className="py-10 text-center text-sm text-muted-foreground">
                            No open repair orders right now
                        </div>
                    ) : (
                        <div className="divide-y">
                            {displayOrders.map((ro) => {
                                const isAssigned = ro.tech_staff_id === staff?.staff_id;
                                return (
                                    <Link
                                        key={ro.ro_id}
                                        to={`/service/repair-orders/${ro.ro_id}`}
                                        className="flex items-start justify-between px-5 py-3 hover:bg-muted/40 transition-colors"
                                    >
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${isAssigned ? "bg-orange-50" : "bg-slate-100"}`}>
                                                <Wrench className={`h-4 w-4 ${isAssigned ? "text-orange-600" : "text-slate-400"}`} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium">RO #{ro.ro_id}</p>
                                                    {isAssigned && (
                                                        <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">Assigned to me</span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {ro.vehicle_desc ?? ro.appointment?.service_type ?? "—"}
                                                </p>
                                                {ro.appointment?.customer_name && (
                                                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                                        <User className="h-3 w-3" />
                                                        {ro.appointment.customer_name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0 ml-3">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[ro.status] ?? "bg-gray-100 text-gray-600"}`}>
                                                {ro.status.replace("_", " ")}
                                            </span>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </Card>

                {/* Today's appointments */}
                <Card className="overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b">
                        <h2 className="font-semibold">Today's Appointments</h2>
                        <span className="text-xs text-muted-foreground">{today}</span>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-10">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : todayAppts.length === 0 ? (
                        <div className="py-10 text-center text-sm text-muted-foreground">
                            No appointments today
                        </div>
                    ) : (
                        <div className="divide-y">
                            {todayAppts.map((a) => (
                                <div key={a.appt_id} className="px-5 py-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <p className="text-sm font-medium">
                                                {a.customer_name ?? "Walk-in"}
                                            </p>
                                            <p className="text-xs text-muted-foreground">
                                                {a.service_type ?? "General Service"}
                                            </p>
                                            {(a.vehicle_year || a.vehicle_make) && (
                                                <p className="text-xs text-muted-foreground mt-0.5">
                                                    {[a.vehicle_year, a.vehicle_make, a.vehicle_model].filter(Boolean).join(" ")}
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="h-3 w-3" />
                                                {a.appt_time ?? "—"}
                                            </div>
                                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium mt-1 inline-block ${APPT_STATUS_COLOR[a.status] ?? "bg-gray-100 text-gray-600"}`}>
                                                {a.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}
